import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { CreateTareaDTO, KeyValue, TareaDTO, TareaPrioridad, TareaStatus, TareaTipo, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { endOfDayFromDateInput, toDateInput, todayIso } from '../../../utils/dates'
import { DESCRIPCION_MAX, errorMessage, fetchSellerOptions, fetchTaskCatalog, NOMBRE_MAX, TASKS_PATH } from '../common/task-common'
import type { FormMessage, SellerOption, TasksLocationState } from '../common/task-common'

export { DESCRIPCION_MAX, NOMBRE_MAX } from '../common/task-common'

export interface TaskFormState {
    nombre: string
    descripcion: string
    vencimiento: string
    prioridad: TareaPrioridad
    tipo: TareaTipo
    status: TareaStatus
    vendedor: string
}

const LOAD_ERROR = 'Error al cargar la página'
const SAVE_ERROR = 'Error al guardar la tarea'
const DELETE_ERROR = 'Error al eliminar la tarea'
const NOT_FOUND = 'No se encontró la tarea.'

const emptyState = (): TaskFormState => ({
    nombre: '',
    descripcion: '',
    vencimiento: todayIso(),
    prioridad: 'media',
    tipo: 'tarea',
    status: 'abierta',
    vendedor: '',
})

const toFormState = (body: TareaDTO): TaskFormState => ({
    nombre: body.nombre || '',
    descripcion: body.descripcion || '',
    vencimiento: toDateInput(body.vencimiento),
    prioridad: body.prioridad?.name || 'media',
    tipo: body.tipo?.name || 'tarea',
    status: body.status?.name || 'abierta',
    vendedor: body.vendedor?.id ? String(body.vendedor.id) : '',
})

const toRequest = (state: TaskFormState, id: number | null): CreateTareaDTO => ({
    id,
    nombre: state.nombre.trim(),
    descripcion: state.descripcion.trim(),
    vencimiento: endOfDayFromDateInput(state.vencimiento),
    prioridad: state.prioridad,
    tipo: state.tipo,
    vendedor: state.vendedor ? Number(state.vendedor) : null,
    status: id ? state.status : 'abierta',
})

export const useTaskFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { tareaId } = useParams()
    const backUrl: string = (location.state as TasksLocationState | null)?.backUrl || TASKS_PATH
    const id = tareaId ? Number(tareaId) || null : null

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [state, setState] = useState<TaskFormState>(emptyState)
    const [prioridades, setPrioridades] = useState<KeyValue[]>([])
    const [tipos, setTipos] = useState<KeyValue[]>([])
    const [statuses, setStatuses] = useState<KeyValue[]>([])
    const [sellers, setSellers] = useState<SellerOption[]>([])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const [prioridadList, tipoList, statusList, sellerList] = await Promise.all([
                    fetchTaskCatalog('/tareas/prioridades'),
                    fetchTaskCatalog('/tareas/tipos'),
                    fetchTaskCatalog('/tareas/status'),
                    fetchSellerOptions(),
                ])
                if (!active) return
                setPrioridades(prioridadList)
                setTipos(tipoList)
                setStatuses(statusList)
                setSellers(sellerList)
                if (!id) {
                    setState(emptyState())
                    return
                }
                const response: WrapperResponse<TareaDTO | null> | null = await (await APIInvoke.invokeGET(`/tareas/${id}`)).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                if (!response.body) throw new Error(NOT_FOUND)
                setState(toFormState(response.body))
            } catch (error) {
                console.error('Error al inicializar la página', error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [id])

    const patch = (partial: Partial<TaskFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        nombre: (value: string) => patch({ nombre: value.slice(0, NOMBRE_MAX) }),
        descripcion: (value: string) => patch({ descripcion: value.slice(0, DESCRIPCION_MAX) }),
        vencimiento: (value: string) => patch({ vencimiento: value }),
        prioridad: (value: string) => patch({ prioridad: value as TareaPrioridad }),
        tipo: (value: string) => patch({ tipo: value as TareaTipo }),
        status: (value: string) => patch({ status: value as TareaStatus }),
        vendedor: (value: string) => patch({ vendedor: value }),
    }

    const canSave = Boolean(state.nombre.trim() && state.descripcion.trim() && state.prioridad && state.tipo)

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!canSave || saving) return
        try {
            setSaving(true)
            setMessage(null)
            const request = toRequest(state, id)
            const response: WrapperResponse<TareaDTO> = id
                ? await (await APIInvoke.invokePUT(`/tareas/${id}`, request)).json()
                : await (await APIInvoke.invokePOST('/tareas', request)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
                return
            }
            history.push({ pathname: TASKS_PATH, state: { flash: id ? 'Tarea actualizada correctamente' : 'Tarea creada correctamente' } satisfies TasksLocationState })
        } catch (error) {
            console.error('Error al guardar', error)
            setMessage({ type: 'error', message: errorMessage(error, SAVE_ERROR) })
        } finally {
            setSaving(false)
        }
    }

    const deleteTask = async () => {
        if (!id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/tareas/${id}`)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
                return
            }
            history.push({ pathname: TASKS_PATH, state: { flash: 'Tarea eliminada correctamente' } satisfies TasksLocationState })
        } catch (error) {
            console.error('Error al eliminar la tarea', error)
            setMessage({ type: 'error', message: errorMessage(error, DELETE_ERROR) })
        } finally {
            setDeleting(false)
        }
    }

    const isEdit = Boolean(id)

    return {
        form: state,
        prioridades,
        tipos,
        statuses,
        sellers,
        loading,
        saving,
        deleting,
        busy: loading || saving || deleting,
        busyLabel: deleting ? 'Eliminando tarea…' : loading ? 'Cargando tarea…' : 'Guardando tarea…',
        message,
        isEdit,
        canSave,
        title: isEdit ? 'Editar tarea' : 'Nueva tarea',
        backTo: backUrl,
        confirmDelete,
        change,
        save,
        cancel: () => history.push(backUrl),
        deleteTask,
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
    }
}
